import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Smartphone,
  CheckCircle,
  Clock,
  AlertCircle,
  Radio,
  Tag,
  Package,
  MapPin,
  User,
  Calendar,
  RefreshCw,
  XCircle,
  Info,
  Box,
  Plus,
  Loader2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Scissors, ClipboardList, Box as BoxIcon, ArrowRight, UserCircle } from "lucide-react";
import { ProcessTimeline } from "@/components/NFC/ProcessTimeline";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { useNFCReader } from "@/hooks/useNFCReader";
import { useScannerContext } from "@/contexts/ScannerContext";
import logger from "@/lib/logger";

interface NFCTagInfo {
  id: string;
  tag_uid: string;
  tag_label: string | null;
  tag_type?: string | null;
  assigned_to_mold_id?: string | null;
  assigned_to_plotter_cut_id?: string | null;
  assigned_to_user_id?: string | null;
  work_order_id?: string | null;
  status: string;
  location: string | null;
  molds?: {
    id: string;
    name: string;
    code: string | null;
  } | null;
  work_orders?: {
    id?: string;
    order_number: string;
    product_name: string;
    status: string;
  } | null;
  manufacturing_processes?: {
    id?: string;
    name: string;
    description: string | null;
  } | null;
  plotter_cuts?: {
    id: string;
    package_code: string;
    package_name: string | null;
    status?: string;
  } | null;
  users?: {
    id: string;
    full_name: string | null;
    email: string | null;
  } | null;
}

interface CaptureResult {
  serialNumber: string;
  tagInfo: NFCTagInfo | null;
  rawData: any;
  recordType: string;
  timestamp: Date;
}

const NFCCaptures = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [lastCapture, setLastCapture] = useState<CaptureResult | null>(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [registeringTag, setRegisteringTag] = useState(false);

  const { isScanning, isSupported, lastRead, startScanning, stopScanning } = useNFCReader();
  const { nfcBridge } = useScannerContext();

  // NFC disponible si hay soporte móvil O lector USB conectado
  const nfcAvailable = isSupported || nfcBridge.isReaderReady;

  // Detectar lectura NFC desde lector USB
  useEffect(() => {
    if (nfcBridge.lastRead) {
      const timeSinceRead = Date.now() - nfcBridge.lastRead.timestamp;
      if (timeSinceRead < 5000) {
        const uid = nfcBridge.lastRead.uid;
        // Procesar lectura USB como si fuera una lectura normal
        processUsbRead(uid);
      }
    }
  }, [nfcBridge.lastRead]);

  const processUsbRead = async (uid: string) => {
    try {
      const { data: tagInfo } = await api
        .from('nfc_tags')
        .select(`
          *,
          molds:assigned_to_mold_id (id, name, code),
          work_orders:work_order_id (id, order_number, product_name, status),
          manufacturing_processes:process_id (id, name, description),
          plotter_cuts:assigned_to_plotter_cut_id (id, package_code, package_name, status, assigned_work_order_id),
          users:assigned_to_user_id (id, full_name, email)
        `)
        .eq('tag_uid', uid)
        .maybeSingleRow();

      setLastCapture({
        serialNumber: uid,
        tagInfo: tagInfo,
        rawData: null,
        recordType: 'usb-bridge',
      });
      setShowResultDialog(true);

      // Guardar captura
      await saveCaptureM.mutateAsync({
        tag_uid: uid,
        operator_id: user?.id,
        raw_data: { source: 'usb-bridge' },
      });

      toast.success('NFC capturado (USB)', { description: `UID: ${uid}` });
    } catch (error) {
      logger.error('Error processing USB NFC read', error);
    }
  };

  // Registrar etiqueta NFC en el sistema
  const handleRegisterTag = async (uid: string) => {
    if (!user) return;

    setRegisteringTag(true);
    try {
      const { error } = await api
        .from('nfc_tags')
        .insert({
          tag_uid: uid,
          tag_label: null,
          status: 'active',
          description: 'Registrado desde Capturas NFC',
          created_by: user.id,
        });

      if (error) {
        if (error.message?.includes('duplicate')) {
          toast.error('Esta etiqueta ya está registrada');
        } else {
          throw error;
        }
      } else {
        toast.success('Etiqueta registrada exitosamente');
        // Actualizar el lastCapture para mostrar que ya está registrada
        if (lastCapture) {
          const { data: tagInfo } = await api
            .from('nfc_tags')
            .select('*')
            .eq('tag_uid', uid)
            .single();

          setLastCapture({
            ...lastCapture,
            tagInfo: tagInfo,
          });
        }
      }
    } catch (error: any) {
      logger.error('Error registering NFC tag', error);
      toast.error('Error al registrar: ' + (error.message || 'Error desconocido'));
    } finally {
      setRegisteringTag(false);
    }
  };

  // Fetch captures history
  const { data: captures, isLoading, refetch } = useQuery({
    queryKey: ['nfc-captures'],
    queryFn: async () => {
      const { data, error } = await api
        .from('nfc_captures')
        .select(`
          *,
          profiles:operator_id (
            full_name,
            employee_id
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Save capture mutation
  const saveCaptureM = useMutation({
    mutationFn: async (capture: {
      serialNumber: string;
      tagInfo: NFCTagInfo | null;
      rawData: any;
    }) => {
      const captureId = `CAP-${Date.now().toString(36).toUpperCase()}`;

      const { error } = await api
        .from('nfc_captures')
        .insert({
          capture_id: captureId,
          device_id: navigator.userAgent.slice(0, 50),
          operator_id: user?.id,
          location: capture.tagInfo?.location || 'Sin ubicación',
          status: capture.tagInfo ? 'completed' : 'processing',
          data_points: {
            serial_number: capture.serialNumber,
            tag_uid: capture.tagInfo?.tag_uid,
            tag_label: capture.tagInfo?.tag_label,
            tag_type: capture.tagInfo?.tag_type || capture.rawData?.tagType,
            mold_id: capture.tagInfo?.assigned_to_mold_id || capture.rawData?.moldId,
            mold_name: capture.tagInfo?.molds?.name,
            mold_code: capture.tagInfo?.molds?.code,
            work_order: capture.tagInfo?.work_orders?.order_number,
            process: capture.tagInfo?.manufacturing_processes?.name,
            raw_data: capture.rawData,
          },
        });

      if (error) throw error;

      // Update last_scanned_at on the tag if found
      if (capture.tagInfo) {
        await api
          .from('nfc_tags')
          .update({
            last_scanned_at: new Date().toISOString(),
            last_scanned_by: user?.id,
          })
          .eq('tag_uid', capture.serialNumber);
      }

      return captureId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfc-captures'] });
    },
    onError: (error: any) => {
      logger.error('Error saving NFC capture:', error);
    },
  });

  // Handle NFC scan start
  const handleStartScan = async () => {
    if (!isSupported && !nfcBridge.isReaderReady) {
      toast.error("NFC no disponible", {
        description: "Conecta un lector USB o usa un dispositivo móvil con NFC",
      });
      return;
    }

    if (nfcBridge.isReaderReady && !isSupported) {
      // Solo lector USB disponible - las lecturas se capturan automáticamente
      toast.info("Lector USB activo", {
        description: "Acerca el chip NFC al lector USB",
      });
      return;
    }

    setShowScanDialog(true);
    setLastCapture(null);
    await startScanning();
  };

  // Handle scan stop
  const handleStopScan = () => {
    stopScanning();
    setShowScanDialog(false);
  };

  // Process NFC read
  useEffect(() => {
    if (lastRead && lastRead.serialNumber) {
      const processCapture = async () => {
        // Search for the tag in nfc_tags
        const { data: tagData } = await api
          .from('nfc_tags')
          .select(`
            *,
            molds:assigned_to_mold_id (id, name, code),
            work_orders:work_order_id (id, order_number, product_name, status),
            manufacturing_processes:process_id (id, name, description),
            plotter_cuts:assigned_to_plotter_cut_id (id, package_code, package_name, status, assigned_work_order_id),
            users:assigned_to_user_id (id, full_name, email)
          `)
          .eq('tag_uid', lastRead.serialNumber)
          .maybeSingleRow();

        const capture: CaptureResult = {
          serialNumber: lastRead.serialNumber,
          tagInfo: tagData || null,
          rawData: lastRead.data,
          recordType: lastRead.recordType,
          timestamp: new Date(),
        };

        setLastCapture(capture);
        setShowScanDialog(false);
        setShowResultDialog(true);
        stopScanning();

        // Save to database
        saveCaptureM.mutate({
          serialNumber: lastRead.serialNumber,
          tagInfo: tagData || null,
          rawData: lastRead.data,
        });

        if (tagData) {
          toast.success("Etiqueta NFC reconocida", {
            description: tagData.tag_label || tagData.tag_uid,
          });
        } else {
          toast.info("Etiqueta NFC detectada", {
            description: `UID: ${lastRead.serialNumber}`,
          });
        }
      };

      processCapture();
    }
  }, [lastRead]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-accent" />;
      case "processing":
        return <Clock className="w-4 h-4 text-primary" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-accent/10 text-accent border-accent/20";
      case "processing":
        return "bg-primary/10 text-primary border-primary/20";
      case "error":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return t.nfcCaptures?.status?.completed || "Completado";
      case "processing":
        return t.nfcCaptures?.status?.processing || "Procesando";
      case "error":
        return t.nfcCaptures?.status?.error || "Error";
      default:
        return status;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-foreground">{t.nfcCaptures?.title || "Capturas NFC"}</h2>
            <p className="text-muted-foreground mt-1">
              {t.nfcCaptures?.subtitle || "Escanea y registra etiquetas NFC"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button
              className="bg-gradient-magma hover:opacity-90 text-white"
              onClick={handleStartScan}
              disabled={!nfcAvailable}
            >
              <Radio className="w-4 h-4 mr-2" />
              {t.nfcCaptures?.newSession || "Escanear NFC"}
            </Button>
          </div>
        </div>

        {/* NFC Support Info */}
        {!isSupported && !nfcBridge.isReaderReady && (
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    NFC no disponible
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Conecta un lector USB y ejecuta el NFC Bridge, o usa un dispositivo móvil con NFC.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {nfcBridge.isReaderReady && (
          <Card className="border-green-500/50 bg-green-500/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-green-700 dark:text-green-400">
                    Lector USB conectado
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Acerca el chip NFC al lector para capturarlo automáticamente.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Capturas</CardTitle>
              <Smartphone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{captures?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent">
                {captures?.filter((c: any) => c.status === 'completed').length || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Hoy</CardTitle>
              <Calendar className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {captures?.filter((c: any) => {
                  const today = new Date();
                  const captureDate = new Date(c.created_at);
                  return captureDate.toDateString() === today.toDateString();
                }).length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Captures List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">{t.nfcCaptures?.loading || "Cargando..."}</p>
          </div>
        ) : captures && captures.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {captures.map((capture: any) => (
              <Card
                key={capture.id}
                className="p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 rounded-xl bg-primary/10">
                      <Smartphone className="w-6 h-6 text-primary" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {capture.capture_id}
                        </h3>
                        <Badge
                          variant="outline"
                          className={getStatusColor(capture.status)}
                        >
                          <span className="mr-1">{getStatusIcon(capture.status)}</span>
                          {getStatusLabel(capture.status)}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">{t.nfcCaptures?.device || "Dispositivo"}</span>
                          <p className="font-medium text-foreground mt-1 truncate max-w-[150px]">
                            {capture.device_id?.slice(0, 20)}...
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t.nfcCaptures?.operator || "Operador"}</span>
                          <p className="font-medium text-foreground mt-1">
                            {capture.profiles?.full_name || t.nfcCaptures?.unknown || "Desconocido"}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t.nfcCaptures?.location || "Ubicacion"}</span>
                          <p className="font-medium text-foreground mt-1">
                            {capture.location}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Tag UID</span>
                          <p className="font-medium text-foreground mt-1 font-mono text-xs">
                            {capture.data_points?.serial_number || capture.data_points?.tag_uid || '-'}
                          </p>
                        </div>
                      </div>

                      {capture.data_points?.work_order && (
                        <div className="mt-3 flex items-center gap-2 text-sm">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Orden:</span>
                          <span className="font-medium">{capture.data_points.work_order}</span>
                          {capture.data_points?.process && (
                            <>
                              <span className="text-muted-foreground mx-1">|</span>
                              <span>{capture.data_points.process}</span>
                            </>
                          )}
                        </div>
                      )}
                      {capture.data_points?.mold_name && (
                        <div className="mt-3 flex items-center gap-2 text-sm">
                          <Box className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Molde:</span>
                          <span className="font-medium">{capture.data_points.mold_name}</span>
                          {capture.data_points?.mold_code && (
                            <span className="text-muted-foreground">({capture.data_points.mold_code})</span>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground mt-3">
                        {format(new Date(capture.created_at), language === "es" ? "d 'de' MMMM, yyyy 'a las' HH:mm" : "MMMM d, yyyy 'at' HH:mm", { locale: language === "es" ? es : enUS })}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Smartphone className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t.nfcCaptures?.noCaptures || "No hay capturas registradas"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t.nfcCaptures?.noCapturesDesc || "Comienza escaneando tu primera etiqueta NFC"}
            </p>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={handleStartScan}
              disabled={!nfcAvailable}
            >
              <Radio className="w-4 h-4 mr-2" />
              {t.nfcCaptures?.startFirst || "Iniciar Escaneo"}
            </Button>
          </Card>
        )}
      </div>

      {/* Scan Dialog */}
      {showScanDialog && (
        <div className="fixed inset-0 bg-gradient-to-br from-background/95 via-background/90 to-primary/10 backdrop-blur-md z-50 flex items-center justify-center animate-in fade-in duration-300">
          <Card className="w-full max-w-md mx-4 glass-card border-primary/30 shadow-magma">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col items-center text-center gap-6">
                {/* Animated NFC Icon */}
                <div className="relative w-32 h-32 flex items-center justify-center">
                  {isScanning && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-gradient-magma opacity-20 animate-ping" style={{ animationDuration: '2s' }}></div>
                      <div className="absolute inset-4 rounded-full bg-gradient-magma opacity-30 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }}></div>
                      <div className="absolute inset-8 rounded-full bg-gradient-magma opacity-40 animate-ping" style={{ animationDuration: '1s', animationDelay: '0.6s' }}></div>
                    </>
                  )}
                  <div className={`relative z-10 ${isScanning ? 'animate-pulse' : ''}`}>
                    <div className="absolute inset-0 bg-gradient-magma blur-xl opacity-50"></div>
                    <Radio className="w-20 h-20 text-primary relative z-10" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                    {isScanning ? "Escaneando..." : "Escaneo NFC"}
                  </h3>
                  {isScanning && (
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-magma-orange animate-bounce" style={{ animationDelay: '0s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-magma-purple animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-magma-orange animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-lg font-medium text-foreground">
                    Acerca tu dispositivo a una etiqueta NFC
                  </p>
                  {isScanning && (
                    <p className="text-sm text-muted-foreground animate-pulse">
                      Manten el dispositivo cerca hasta que vibre
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleStopScan}
                  variant="outline"
                  size="lg"
                  className="mt-4 w-full border-primary/30 hover:bg-primary/10"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Result Dialog */}
      {showResultDialog && lastCapture && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center animate-in fade-in duration-300">
          <Card className="w-full max-w-lg mx-4 shadow-xl">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                {lastCapture.tagInfo ? (
                  <div className="p-3 rounded-full bg-accent/10">
                    <CheckCircle className="w-6 h-6 text-accent" />
                  </div>
                ) : (
                  <div className="p-3 rounded-full bg-primary/10">
                    <Info className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div>
                  <CardTitle>
                    {lastCapture.tagInfo ? "Etiqueta Reconocida" : "Etiqueta Detectada"}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {format(lastCapture.timestamp, "dd/MM/yyyy HH:mm:ss")}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* UID */}
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">UID de Etiqueta</span>
                </div>
                <p className="font-mono text-lg">{lastCapture.serialNumber}</p>
              </div>

              {lastCapture.tagInfo ? (
                <>
                  {/* Tag Label */}
                  {lastCapture.tagInfo.tag_label && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-muted-foreground">Nombre</span>
                      <span className="font-medium">{lastCapture.tagInfo.tag_label}</span>
                    </div>
                  )}

                  {/* Work Order */}
                  {lastCapture.tagInfo.work_orders && (
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardList className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Asignado a Orden de Trabajo</span>
                      </div>
                      <p className="font-semibold">{lastCapture.tagInfo.work_orders.order_number}</p>
                      <p className="text-sm text-muted-foreground">{lastCapture.tagInfo.work_orders.product_name}</p>
                      {lastCapture.tagInfo.work_orders.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full"
                          onClick={() => {
                            setShowResultDialog(false);
                            navigate(`/work-orders?open=${lastCapture.tagInfo!.work_orders!.id}`);
                          }}
                        >
                          Ver OT <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Mold */}
                  {lastCapture.tagInfo.molds && (
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <BoxIcon className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Asignado a Molde</span>
                      </div>
                      <p className="font-semibold">{lastCapture.tagInfo.molds.name}</p>
                      {lastCapture.tagInfo.molds.code && (
                        <p className="text-sm text-muted-foreground">{lastCapture.tagInfo.molds.code}</p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={() => {
                          setShowResultDialog(false);
                          navigate(`/molds?focus=${lastCapture.tagInfo!.molds!.id}`);
                        }}
                      >
                        Ver molde <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}

                  {/* Plotter cut */}
                  {lastCapture.tagInfo.plotter_cuts && (
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Scissors className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Asignado a Paquete Plotter</span>
                      </div>
                      <p className="font-semibold">{lastCapture.tagInfo.plotter_cuts.package_code}</p>
                      {lastCapture.tagInfo.plotter_cuts.package_name && (
                        <p className="text-sm text-muted-foreground">{lastCapture.tagInfo.plotter_cuts.package_name}</p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={() => {
                          setShowResultDialog(false);
                          navigate(`/plotter-cuts?focus=${lastCapture.tagInfo!.plotter_cuts!.id}`);
                        }}
                      >
                        Ver paquete <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}

                  {/* Usuario (fichaje) */}
                  {lastCapture.tagInfo.users && (
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCircle className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Tarjeta de Fichaje</span>
                      </div>
                      <p className="font-semibold">{lastCapture.tagInfo.users.full_name || lastCapture.tagInfo.users.email}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={() => {
                          setShowResultDialog(false);
                          navigate(`/fichaje/admin?user=${lastCapture.tagInfo!.users!.id}`);
                        }}
                      >
                        Ver fichaje <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}

                  {/* Process */}
                  {lastCapture.tagInfo.manufacturing_processes && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-muted-foreground">Proceso</span>
                      <span className="font-medium">{lastCapture.tagInfo.manufacturing_processes.name}</span>
                    </div>
                  )}

                  {/* Location */}
                  {lastCapture.tagInfo.location && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Ubicacion</span>
                      </div>
                      <span className="font-medium">{lastCapture.tagInfo.location}</span>
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-muted-foreground">Estado</span>
                    <Badge variant="outline" className={
                      lastCapture.tagInfo.status === 'active'
                        ? "bg-accent/10 text-accent"
                        : "bg-muted text-muted-foreground"
                    }>
                      {lastCapture.tagInfo.status === 'active' ? 'Activa' : lastCapture.tagInfo.status}
                    </Badge>
                  </div>

                  {/* Timeline del flujograma */}
                  <div className="pt-3 border-t">
                    <ProcessTimeline
                      workOrderId={
                        (lastCapture.tagInfo.work_orders?.id as any) ||
                        (lastCapture.tagInfo.plotter_cuts as any)?.assigned_work_order_id ||
                        null
                      }
                    />
                  </div>
                </>
              ) : (
                <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-700 dark:text-amber-400">
                      Esta etiqueta no esta registrada en el sistema
                    </span>
                  </div>
                  <Button
                    onClick={() => handleRegisterTag(lastCapture.serialNumber)}
                    disabled={registeringTag}
                    className="w-full mt-3"
                    variant="default"
                  >
                    {registeringTag ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Registrar en Sistema
                  </Button>
                </div>
              )}

              {/* Raw Data */}
              {lastCapture.rawData && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Ver datos crudos
                  </summary>
                  <pre className="mt-2 p-3 rounded bg-muted text-xs overflow-auto max-h-32">
                    {JSON.stringify(lastCapture.rawData, null, 2)}
                  </pre>
                </details>
              )}
            </CardContent>
            <div className="p-6 pt-0 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowResultDialog(false);
                  handleStartScan();
                }}
              >
                <Radio className="w-4 h-4 mr-2" />
                Escanear Otra
              </Button>
              <Button
                className="flex-1"
                onClick={() => setShowResultDialog(false)}
              >
                Cerrar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
};

export default NFCCaptures;
